import { setFilter, on } from "./store.js";

/**
 * Prevent double-initialization if this module is ever imported twice.
 * (Harmless with single entry app.js, but keeps things robust.)
 */
(function initIncipitSearch() {
  if (window.__incipitSearchInit) return;
  window.__incipitSearchInit = true;

  /** -------------------------------------------------------------
   * Constants & element refs
   * --------------------------------------------------------------*/

  /** MIDI note for C4 (middle C) */
  const PIANO_START = 48;
  /** MIDI note for C6 */
  const PIANO_END = 84;

  /** Pitch-class names for MIDI % 12 */
  const NOTE_NAMES = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];

  const elPiano = /** @type {HTMLElement|null} */ (
    document.getElementById("piano")
  );
  const elPattern = /** @type {HTMLInputElement|null} */ (
    document.getElementById("incipit-pattern")
  );
  const elApply = /** @type {HTMLButtonElement|null} */ (
    document.getElementById("incipit-apply")
  );
  const elClear = /** @type {HTMLButtonElement|null} */ (
    document.getElementById("incipit-clear")
  );
  const elBack = /** @type {HTMLButtonElement|null} */ (
    document.getElementById("incipit-back")
  );
  const elCount = /** @type {HTMLElement|null} */ (
    document.getElementById("incipit-count")
  );

  /** -------------------------------------------------------------
   * Helpers
   * --------------------------------------------------------------*/

  /**
   * Convert a MIDI number to a note name with octave (e.g., 64 → "E4").
   * @param {number} m - MIDI number
   * @returns {string} - Note name with octave
   */
  function midiToName(m) {
    const pc = m % 12;
    const oct = Math.floor(m / 12) - 1; // MIDI standard: C4 = 60 → 60/12 - 1 = 4
    return NOTE_NAMES[pc] + oct;
  }

  /** -------------------------------------------------------------
   * Piano rendering
   * --------------------------------------------------------------*/

  /**
   * Render a simple clickable piano (C4..C6) into a container.
   * Clicking a key appends its name+octave to the pattern input.
   * @param {HTMLElement|null} container
   * @param {number} [start=PIANO_START]
   * @param {number} [end=PIANO_END]
   */
  function drawPiano(container, start = PIANO_START, end = PIANO_END) {
    if (!container) return;
    container.innerHTML = "";

    const wrap = document.createElement("div");
    wrap.style.position = "relative";
    wrap.style.height = "100%";

    // Determine white vs. black keys by pitch-class
    const whiteOrder = [0, 2, 4, 5, 7, 9, 11]; // C D E F G A B
    const isBlack = (pc) => !whiteOrder.includes(pc);

    // White keys (layout grid)
    const whites = [];
    for (let m = start; m <= end; m++) if (!isBlack(m % 12)) whites.push(m);

    whites.forEach((m, idx) => {
      const k = document.createElement("button");
      k.type = "button";
      k.dataset.midi = String(m);
      k.className = "white";
      Object.assign(k.style, {
        position: "absolute",
        left: idx * 28 + "px",
        top: "0",
        width: "28px",
        height: "100%",
        border: "1px solid #bbb",
        background: "#fff",
      });
      k.addEventListener("click", () => pushNote(m));
      wrap.appendChild(k);
    });

    // Black keys (positioned between whites)
    let whiteIdx = 0;
    for (let m = start; m <= end; m++) {
      const pc = m % 12;
      if (!isBlack(pc)) {
        whiteIdx++;
        continue;
      }

      const left = whiteIdx * 28 - 10;
      const k = document.createElement("button");
      k.type = "button";
      k.dataset.midi = String(m);
      k.className = "black";
      Object.assign(k.style, {
        position: "absolute",
        left: left + "px",
        top: "0",
        width: "20px",
        height: "65%",
        border: "1px solid #444",
        background: "#111",
        color: "#fff",
        zIndex: "2",
      });
      k.addEventListener("click", () => pushNote(m));
      wrap.appendChild(k);
    }

    container.style.position = "relative";
    container.style.overflow = "hidden";
    container.appendChild(wrap);
  }

  /**
   * Append a note to the pattern input (e.g., "E5").
   * @param {number} midi
   */
  function pushNote(midi) {
    if (!elPattern) return;
    elPattern.value += midiToName(midi);
  }

  /** -------------------------------------------------------------
   * Filter wiring
   * --------------------------------------------------------------*/

  /**
   * Apply the current pattern / position / transpose values to the store.
   * Also toggles a small active badge when a pattern is present.
   */
  function applyFilter() {
    if (!elPattern) return;

    const patt = elPattern.value.trim();
    const pos =
      /** @type {HTMLInputElement|null} */ (
        document.querySelector('input[name="pos"]:checked')
      )?.value || "start";
    const transp =
      /** @type {HTMLInputElement|null} */ (
        document.querySelector('input[name="transp"]:checked')?.value || "off"
      ) === "on";

    setFilter({
      incipitPattern: patt,
      position: pos,
      transpose: transp,
    });

    if (elCount) elCount.textContent = patt ? "(Filter aktiv)" : "";
  }

  /** -------------------------------------------------------------
   * Boot & event listeners
   * --------------------------------------------------------------*/

  // Render keyboard on load
  drawPiano(elPiano);

  // Button: Apply (prevent form submission double-trigger)
  elApply?.addEventListener("click", (e) => {
    e.preventDefault();
    applyFilter();
  });

  // Button: Clear
  elClear?.addEventListener("click", () => {
    if (elPattern) elPattern.value = "";
    if (elCount) elCount.textContent = "";
    applyFilter();
  });

  // Button: Backspace one token (single character)
  elBack?.addEventListener("click", () => {
    if (!elPattern) return;
    elPattern.value = elPattern.value.slice(0, -1);
  });

  // Clear the little badge when fresh data arrives
  on("data:ready", () => {
    if (elCount) elCount.textContent = "";
  });
})();
